/**
 * Components Screen (Data Groups)
 *
 * Shows all data collections (groups of uploaded files with a shared column
 * mapping) and lets the user include or exclude each one from the master
 * dataset. The "Build Master" button merges all included collections into
 * `master.parquet.gzip` on the Python backend.
 */
import { useSelector, useDispatch } from "react-redux";
import { useState } from "react";
// --------------------------------------------------------------
import {
  includeCollection,
  excludeCollection,
} from "Redux/collections/actions";
import { mergeCollectionsIntoFrame } from "Redux/frame/actions";
// --------------------------------------------------------------

// --------------------------------------------------------------
// --------------------------------------------------------------
const Components = () => {
  const dispatch = useDispatch();
  const [building, setBuilding] = useState(false);
  const { collections, included } = useSelector((state) => state.project);

  const nFiles = collections.reduce((acc, collection) => {
    return acc + collection.fileids.length;
  }, 0);
  const nIncluded = included.length;

  return (
    <div>
      <div className="table-container">
        <table className="table is-striped is-hover">
          <thead>
            <tr>
              <th>Data Group</th>
              <th>Signature</th>
              <th>Files</th>
              <th>Canonicals</th>
              <th>Included</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {collections.map((collection) => {
              const canonicals = collection.mapping
                ? Object.keys(collection.mapping).length
                : 0;

              return (
                <tr key={collection.id}>
                  <td>{collection.title}</td>
                  <td></td>
                  <td>{collection.fileids.length}</td>
                  {canonicals > 0 ? (
                    <td>{canonicals}</td>
                  ) : (
                    <td>
                      <div className="tag is-warning">
                        0
                        <i className="fa-duotone fa-exclamation-triangle i-warning" />
                      </div>
                    </td>
                  )}
                  {included.includes(collection.id) ? (
                    <>
                      <td>
                        <div className="tag is-success is-light">
                          <span>
                            <i className="fa-duotone fa-check" />
                          </span>
                        </div>
                      </td>
                      <td>
                        <button
                          className="button is-small"
                          onClick={() => {
                            dispatch(excludeCollection(collection.id));
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    </>
                  ) : (
                    <ExcludedCollection
                      collection={collection}
                      canonicals={canonicals}
                    />
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={2}>Total</th>
              <th>{nFiles}</th>
              <th></th>
              <th>{nIncluded}</th>
              <th></th>
            </tr>
            <tr>
              <td colSpan={6}>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    className={`button is-small ${
                      building ? "is-loading" : ""
                    }`}
                    disabled={nFiles === 0 || nIncluded === 0}
                    onClick={() => {
                      setBuilding(true);
                      dispatch(
                        mergeCollectionsIntoFrame(() => {
                          setBuilding(false);
                        })
                      );
                    }}
                  >
                    <span className="icon is-small">
                      <i className="fa-duotone fa-database" />
                    </span>
                    <span>Build Master</span>
                  </button>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default Components;

// --------------------------------------------------------------
// ExcludedCollection — Row actions for a collection not yet in the master.
// The "Add" button is disabled when canonicals === 0 (no column mapping).
// --------------------------------------------------------------
const ExcludedCollection = ({ collection, canonicals }) => {
  const dispatch = useDispatch();
  const [including, setIncluding] = useState(false);

  const icon = including ? "fa-spinner fa-spin" : "fa-times";
  const visibility = including ? "hidden" : "visible";

  return (
    <>
      <td>
        <div className="tag">
          <i className={`fa-duotone fa-times ${icon}`} />
        </div>
      </td>
      <td>
        <button
          style={{ visibility }}
          className="button is-small"
          onClick={() => {
            if (canonicals > 0) {
              setIncluding(true);
              dispatch(includeCollection(collection.id, () => {
                setIncluding(false);
              }));
            }
          }}
        >
          Add
        </button>
      </td>
    </>
  );
};
